import React from 'react';
import { useNavigate } from 'react-router-dom';
import TranslationRequestCard from './TranslationRequestCard';

const MessageBubble = ({ message, isUser = false, licenceId }) => {
  const navigate = useNavigate();
  
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${String(minutes).padStart(2, '0')} ${ampm}`;
  };

  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (message.type === 'status' || message.type === 'system') {
    return (
      <div className="flex items-center gap-2 mb-4">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#10B981" strokeWidth="1.5">
          <path d="M13.3333 4L6 11.3333L2.66667 8" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        <span className="text-sm text-gray-600">{message.body || message.content}</span>
        <span className="text-xs text-gray-400">{formatTime(message.timestamp || message.createdAt)}</span>
      </div>
    );
  }

  if (isUser || message.fromCustomer) {
    const fromCustomer = typeof message.fromCustomerId === 'object' ? message.fromCustomerId : null;
    const senderName = fromCustomer?.firstName && fromCustomer?.lastName
      ? `${fromCustomer.firstName} ${fromCustomer.lastName}`
      : (message.customerName || (message.fromCustomerId ? 'Customer' : 'You'));
    
    // Check if this is a license request message
    const isLicenseRequest = message.messageType === 'licence_request' || 
                             message.meta?.type === 'license' || 
                             message.card?.type === 'license';
    
    // Check if this is a translation request message
    const isTranslationRequest = message.messageType === 'translation_request' || 
                                  message.meta?.type === 'translation_request' || 
                                  message.card?.type === 'translation_request';
    
    return (
      <div className="flex flex-col items-end gap-2 mb-6">
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{formatTime(message.timestamp || message.createdAt)}</span>
          <span className="text-sm font-medium text-gray-900">{senderName}</span>
        </div>
        
        {isTranslationRequest ? (
          <TranslationRequestCard 
            data={{
              selectedLanguages: message.meta?.selectedLanguages || message.meta?.translations || [],
              additionalRequirement: message.meta?.additionalRequirement,
              customTranslationsText: message.meta?.customTranslationsText,
              isUrgent: message.meta?.isUrgent || false,
              territory: message.meta?.territory || ''
            }}
            translationRequestId={message.meta?.translationRequestId}
            licenceId={licenceId || message.meta?.licenceId}
          />
        ) : isLicenseRequest ? (
          <div className="bg-white border border-gray-200 rounded-lg p-5 max-w-[600px] shadow-sm">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#73827C" strokeWidth="1.5">
                  <path d="M13.3333 4.66667H2.66667C2.29848 4.66667 2 4.96515 2 5.33333V12C2 12.3682 2.29848 12.6667 2.66667 12.6667H13.3333C13.7015 12.6667 14 12.3682 14 12V5.33333C14 4.96515 13.7015 4.66667 13.3333 4.66667Z" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10.6667 4.66667V3.33333C10.6667 3.06812 10.5524 2.81377 10.3492 2.62373C10.1461 2.43369 9.87681 2.33333 9.6 2.33333H6.4C6.12319 2.33333 5.85386 2.43369 5.65074 2.62373C5.44762 2.81377 5.33333 3.06812 5.33333 3.33333V4.66667" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="text-sm font-semibold text-gray-900">Licence Request</span>
              </div>
              {(message.meta?.dateRange || message.card?.dateRange) && (
                <span className="text-sm text-gray-500">{(message.meta?.dateRange || message.card?.dateRange)}</span>
              )}
            </div>

            {/* Organization Type */}
            {(message.meta?.organizationType || message.card?.organizationType) && (
              <div className="mb-3">
                <div className="flex items-start gap-2">
                  <span className="text-sm text-gray-600 font-medium">Organisation Type:</span>
                  <span className="text-sm text-gray-900 flex-1">{(message.meta?.organizationType || message.card?.organizationType)}</span>
                </div>
              </div>
            )}

            {/* Translations */}
            {((message.meta?.translations && message.meta.translations.length > 0) || 
              (message.card?.translations && message.card.translations.length > 0)) && (
              <div className="mb-4">
                <div className="flex items-start gap-2 mb-2">
                  <span className="text-sm text-gray-600 font-medium">Translations:</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(message.meta?.translations || message.card?.translations || []).map((lang, idx) => (
                    <span 
                      key={idx}
                      className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-md border border-gray-200"
                    >
                      {lang}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* View Details Button */}
            <button 
              onClick={() => {
                // Get licenceId from various possible sources
                let id = licenceId;
                if (!id) {
                  // Check message.licenceId (could be object or string)
                  if (message.licenceId) {
                    id = typeof message.licenceId === 'object' 
                      ? (message.licenceId._id || message.licenceId.id || message.licenceId.toString())
                      : message.licenceId.toString();
                  } else if (message.meta?.licenceId) {
                    id = message.meta.licenceId.toString();
                  } else if (message.card?.licenceId) {
                    id = message.card.licenceId.toString();
                  }
                } else {
                  id = id.toString();
                }
                
                if (id) {
                  navigate(`/licence-details/${id}`);
                } else if (message.card?.onViewDetails) {
                  message.card.onViewDetails();
                }
              }}
              className="text-sm font-medium text-emerald-600 hover:text-emerald-700 transition-colors mt-2"
            >
              View Details
            </button>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-700 max-w-[80%]">
            {message.body}
          </div>
        )}
      </div>
    );
  }

  // Admin message - LEFT side, WHITE background
  const senderName = message.fromUserId?.firstName && message.fromUserId?.lastName
    ? `${message.fromUserId.firstName} ${message.fromUserId.lastName}`
    : (message.adminName || 'Admin');
  
  const initials = getInitials(senderName);

  return (
    <div className="flex items-start gap-3 mb-6">
      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
        <span className="text-xs font-medium text-gray-600">{initials}</span>
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-sm font-medium text-gray-900">{senderName}</span>
          <span className="text-xs text-gray-500">{formatTime(message.timestamp || message.createdAt)}</span>
        </div>
        
        {message.card && message.card.type === 'quote' ? (
          <div className="bg-white border border-gray-200 rounded-lg p-4 mb-2">
            <p className="text-sm text-gray-700 mb-3">{message.body}</p>
            <div className="space-y-2">
              {message.card.quoteType && (
                <>
                  <p className="text-xs text-emerald-600 font-medium">Quote Type</p>
                  <p className="text-sm text-gray-700">{message.card.quoteType}</p>
                </>
              )}
              {message.card.dueDate && (
                <>
                  <p className="text-xs text-emerald-600 font-medium">Due Date</p>
                  <p className="text-sm text-gray-700">{message.card.dueDate}</p>
                </>
              )}
              {message.card.amount && (
                <>
                  <p className="text-xs text-emerald-600 font-medium">Amount</p>
                  <p className="text-sm font-semibold text-gray-900">{message.card.amount}</p>
                </>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-700">
            {message.body}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessageBubble;

